/**
 * Labstep
 *
 * @module screens/ProtocolCollection/Index/Template
 * @desc ProtocolCollection template index
 */

import EntitySearch from 'labstep-web/components/Entity/Search';
import { BulkAction } from 'labstep-web/components/Entity/Search/BulkActions';
import { EntityTable } from 'labstep-web/components/Entity/Table';
import { ProtocolLabelStatus } from 'labstep-web/components/Protocol/Label/Status';
import { ProtocolCollectionActionCreateTemplate } from 'labstep-web/components/ProtocolCollection/Action/CreateTemplate';
import {
  Action,
  ProtocolCollectionActionMenu,
} from 'labstep-web/components/ProtocolCollection/Action/Menu';
import styles from 'labstep-web/components/ProtocolCollection/Table/styles.module.scss';
import { ProtocolCollectionTemplateEmptyState } from 'labstep-web/components/ProtocolCollectionTemplate/EmptyState';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import type React from 'react';
import { useMemo } from 'react';
import type { IProtocolCollectionIndexTemplateProps } from './types';

export const ProtocolCollectionIndexTemplate: React.FC<
  IProtocolCollectionIndexTemplateProps
> = ({ group }) => {
  const params = useMemo(
    () => ({ group_id: group.id, is_template: true }),
    [group.id],
  );
  const noResultsMessage = useMemo(
    () => <ProtocolCollectionTemplateEmptyState />,
    [],
  );
  const action = useMemo(
    () => <ProtocolCollectionActionCreateTemplate />,
    [],
  );
  const searchBarProps = useMemo(
    () => ({ placeholder: 'Search templates...' }),
    [],
  );
  return (
    <EntitySearch
      params={params}
      noResultsMessage={noResultsMessage}
      action={action}
      entityName={ProtocolCollection.entityName}
      bulkActions={[
        BulkAction.delete,
        BulkAction.transfer_ownership,
        BulkAction.share,
      ]}
      searchBarProps={searchBarProps}
    >
      {({ entities, ...rest }): React.ReactElement => (
        <EntityTable
          {...rest}
          dataCy="components/ProtocolCollection/Index/Template"
          id="protocol-collection-template-table"
          entities={entities}
          extraColumns={[
            {
              header: '',
              content: (protocolCollection: ProtocolCollection) => (
                <div className={styles.container}>
                  {protocolCollection.draft_version && (
                    <ProtocolLabelStatus
                      protocol={protocolCollection.draft_version}
                      showVersionLabel
                    />
                  )}
                  {protocolCollection.last_version && (
                    <ProtocolLabelStatus
                      protocol={protocolCollection.last_version}
                      showVersionLabel
                    />
                  )}
                </div>
              ),
            },
          ]}
          editableName
          actionMenu={({ entity }) => (
            <ProtocolCollectionActionMenu
              protocolCollection={entity}
              actions={[
                Action.view,
                Action.delete,
                Action.share,
                Action.transfer_ownership,
                Action.duplicate,
              ]}
            />
          )}
        />
      )}
    </EntitySearch>
  );
};
